/**
 * Programatický "ding" přes Web Audio API.
 *
 * - Žádný external asset (žádný .mp3 fetch / bundle overhead)
 * - Respektuje browser autoplay policy — AudioContext se vytvoří/resume teprve
 *   po user gesture (klik na bubble). Pokud zpráva přijde dřív, ding se přeskočí.
 * - Sine wave + krátký envelope = subtle, žádný disruptive alert.
 */

type DingPlayer = {
  unlock(): void
  play(): void
}

export function createDingPlayer(): DingPlayer {
  let ctx: AudioContext | null = null

  return {
    unlock() {
      // Spustí AudioContext po user gesture (open chat). Bez tohohle browser
      // blokuje autoplay a console naplive 'AudioContext was not allowed to start'.
      if (ctx) {
        if (ctx.state === 'suspended') void ctx.resume()
        return
      }
      try {
        const AC =
          window.AudioContext ??
          (window as typeof window & { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext
        if (!AC) return
        ctx = new AC()
      } catch {
        /* unsupported */
      }
    },

    play() {
      if (!ctx) return
      // Pokud byl ctx suspended (autoplay policy, tab inactive, …), pokus o resume.
      // Resume vyžaduje předchozí user gesture (= unlock byl zavolán z kliku),
      // jinak browser zamítne — pak prostě silent.
      if (ctx.state === 'suspended') {
        void ctx.resume()
      }
      if (ctx.state !== 'running') return
      try {
        const now = ctx.currentTime

        // Dvě noty (C6 → E6) → "di-ding" feeling
        playTone(ctx, 1046.5, now, 0.08, 0.18)
        playTone(ctx, 1318.5, now + 0.09, 0.12, 0.18)
      } catch {
        /* shouldn't happen if state === 'running' */
      }
    },
  }
}

function playTone(
  ctx: AudioContext,
  freq: number,
  startAt: number,
  durationSec: number,
  peakGain: number,
): void {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.connect(gain)
  gain.connect(ctx.destination)
  osc.type = 'sine'
  osc.frequency.value = freq

  // Envelope: attack 10ms → hold → exponential decay → zero
  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationSec)

  osc.start(startAt)
  osc.stop(startAt + durationSec + 0.05)
}
